/**
 * DK roster ordering utility.
 *
 * DraftKings NFL classic lineups render in a fixed slot order:
 *   QB · RB · RB · WR · WR · WR · TE · FLEX (RB/WR/TE) · DST
 *
 * Backend optimizers emit a `players` array in whatever order the ILP
 * assigned them (typically bucketed by position). This util re-orders
 * the array to match DK's canonical display and attaches a
 * `roster_slot` label so the UI can render "FLEX" instead of the
 * player's raw position when they occupy the flex slot.
 */
export function sortPlayersDKOrder(players) {
  // Showdown lineups (1 CPT + 5 FLEX) keep the engine's slots verbatim — the
  // classic slot sorter would drop the extra WR/TE and lose the captain.
  if ((players || []).some((p) => String(p?.roster_slot || "").toUpperCase() === "CPT")) {
    const cpt = players.filter((p) => String(p.roster_slot).toUpperCase() === "CPT");
    const flex = players.filter((p) => String(p.roster_slot).toUpperCase() !== "CPT");
    return [...cpt, ...flex.map((p) => ({ ...p, roster_slot: "FLEX" }))];
  }
  const buckets = { QB: [], RB: [], WR: [], TE: [], DST: [] };
  (players || []).forEach((p) => {
    let pos = String(p?.position || "").toUpperCase();
    if (pos === "D" || pos === "DEF") pos = "DST";
    (buckets[pos] || buckets.DST).push(p);
  });
  // Sort each bucket by projection desc so the highest-projected RB
  // becomes RB1, next RB2, and the leftover RB drops into FLEX.
  const projOf = (p) => Number(p?.projection ?? p?.our_proj ?? 0);
  Object.keys(buckets).forEach((k) => buckets[k].sort((a, b) => projOf(b) - projOf(a)));

  const out = [];
  const push = (p, slot) => { if (p) out.push({ ...p, roster_slot: slot }); };

  push(buckets.QB.shift(), "QB");
  push(buckets.RB.shift(), "RB");
  push(buckets.RB.shift(), "RB");
  push(buckets.WR.shift(), "WR");
  push(buckets.WR.shift(), "WR");
  push(buckets.WR.shift(), "WR");
  push(buckets.TE.shift(), "TE");

  // FLEX = the single remaining RB/WR/TE. Pick the highest-projected
  // leftover so the FLEX slot is deterministic.
  const flexPool = [...buckets.RB, ...buckets.WR, ...buckets.TE]
    .sort((a, b) => projOf(b) - projOf(a));
  push(flexPool.shift(), "FLEX");

  push(buckets.DST.shift(), "DST");
  return out;
}
